import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider, type AppCheck } from 'firebase/app-check';
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager, memoryLocalCache, connectFirestoreEmulator } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { setAnalytics } from './utils/analytics';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

// App Check only in production web (or when explicitly requested in dev via VITE_APPCHECK_IN_DEV)
// On native platforms (Capacitor), web ReCaptcha is skipped.
const appCheckSiteKey = (!Capacitor.isNativePlatform() && (!import.meta.env.DEV || import.meta.env.VITE_APPCHECK_IN_DEV === 'true'))
  ? (import.meta.env.VITE_APPCHECK_SITE_KEY as string | undefined)
  : undefined;

if (import.meta.env.DEV && appCheckSiteKey) {
  (self as unknown as Record<string, unknown>).FIREBASE_APPCHECK_DEBUG_TOKEN =
    (import.meta.env.VITE_APPCHECK_DEBUG_TOKEN as string | undefined) ?? true;
}

export const appCheck: AppCheck | undefined = (() => {
  if (!appCheckSiteKey || Capacitor.isNativePlatform()) return undefined;
  try {
    return initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    console.warn('[appcheck] initialization skipped:', err);
    return undefined;
  }
})();

export const db = initializeFirestore(app, {
  localCache: import.meta.env.DEV
    ? memoryLocalCache()
    : persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});

if (import.meta.env.DEV && import.meta.env.VITE_SKIP_EMULATOR !== 'true') {
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
}

// Analytics only in production and only if measurementId is configured
if (!import.meta.env.DEV && firebaseConfig.measurementId) {
  setTimeout(() => {
    import('firebase/analytics').then(({ getAnalytics, isSupported, logEvent }) => {
      isSupported().then(yes => {
        if (yes) setAnalytics(getAnalytics(app), logEvent);
      });
    });
  }, 2500);
}
